import React from "react";
import {
  Box,
  BoxProps,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import InvitesService from "../../services/InvitesService";
import { FieldError } from "../fields/FieldError";
import { ValidatedInviteCodeDto } from "@growth-machine-llc/stridist-api-client";
import LoadingButton from "../button/LoadingButton";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  container: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    width: "100%",
  },

  inputSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
  },

  codeChars: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },

  codeChar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    flexShrink: 0,
    flexGrow: 0,
    "& fieldset": {
      borderRadius: theme.spacing(0.5),
    },
    "& input": {
      fontSize: 24,
      fontWeight: 600,
      textAlign: "center",
      backgroundColor: "transparent !important",
      textTransform: "uppercase",
      padding: theme.spacing(1),
    },
  },

  labelSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },

  label: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },

  description: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },

  errorContainer: {
    minHeight: theme.spacing(3),
  },
}));

export interface EnterProgramInviteCodeProps extends BoxProps {
  onCodeSubmit?: (code: string) => void;
  onSuccess?: (invite: ValidatedInviteCodeDto) => void;
  codeLength?: number;
}

export const EnterProgramInviteCode = ({
  className,
  onSuccess,
  codeLength = 5,
  ...other
}: EnterProgramInviteCodeProps) => {
  const s = useStyles();
  const [code, setCode] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");

  const chars = [...new Array(codeLength)].map(
    (_, index) => code.substring(index, index + 1) || "",
  );

  const { isPending: isValidating, mutate: validateCode } = useMutation({
    mutationFn: InvitesService.verifyInviteCode,
    onSuccess: (invite: ValidatedInviteCodeDto) => {
      setErrorMessage("");
      if (onSuccess) {
        onSuccess(invite);
      }
    },
    onError: () => {
      setErrorMessage("Invalid program invite code. Please try again.");
    },
  });

  const disabled = code.length !== codeLength || isValidating;
  const currentIndex = Math.min(code.length, codeLength - 1);

  const handleVerify = React.useCallback(() => {
    validateCode(code);
  }, [code, validateCode]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const key = event.key;

      setErrorMessage("");

      if (key === "Backspace") {
        setCode((code) => code.substring(0, code.length - 1));
      }

      if (key === "Enter" && code.length === codeLength) {
        validateCode(code);
      }
    },
    [code.length, codeLength, validateCode],
  );

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedText = event.clipboardData.getData("text");
      setCode(pastedText.substring(0, codeLength).toUpperCase());
    },
    [codeLength],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const chars = event.target.value;

      setCode((value) =>
        (value.substring(0, currentIndex + 1) + chars)
          .substring(0, codeLength)
          .toUpperCase(),
      );
    },
    [codeLength, currentIndex],
  );

  const focusAtLastChar = React.useCallback(() => {
    const el = document.querySelector(
      `[data-char-index="${currentIndex}"] input`,
    );

    if (el && el !== document.activeElement && !isValidating) {
      (el as HTMLInputElement).focus();
    }
  }, [isValidating, currentIndex]);

  React.useEffect(() => {
    focusAtLastChar();
  }, [code.length, focusAtLastChar]);

  return (
    <Box className={clsx(s.root, className)} {...other}>
      <Box className={s.inputSection}>
        <Box className={s.codeChars}>
          {chars.map((char, index) => (
            <TextField
              key={index}
              className={s.codeChar}
              variant="outlined"
              data-char-index={index}
              value={char}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              onPaste={index === 0 ? handlePaste : undefined}
              autoFocus={index === currentIndex}
              onFocus={focusAtLastChar}
              disabled={isValidating}
            />
          ))}
        </Box>
        <Box className={s.errorContainer}>
          {errorMessage && <FieldError hideIcon>{errorMessage}</FieldError>}
        </Box>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleVerify}
          disabled={disabled}
          fullWidth
        >
          Verify
        </LoadingButton>
      </Box>
    </Box>
  );
};

EnterProgramInviteCode.displayName = "EnterProgramInviteCode";
